import { type DomainConfig, PRODUCTION_DOMAIN, buildDomain } from "@performyard/utils-isomorphic";

type PyEnvironment = {
	release?: string;
	stage?: string;
	domain?: DomainConfig | null;
};

const getPyEnvironment = (): PyEnvironment =>
	(window as Window & { PYENVIRONMENT?: PyEnvironment }).PYENVIRONMENT ?? {};

/** Served from a developer machine — a fact about the browser, not the deployment. */
const isLocalHost = (): boolean => window.location.hostname === "localhost";

/**
 * Groups an environment stage into the shared deployments it talks to. Distinct
 * from the deployment tier's realms: dev environments group under "staging" here
 * because that is the realm whose engagement survey and media CDN they use.
 */
export const getEnvironmentFromStage = (stage: string): string => {
	if (!stage) {
		return "production";
	}

	switch (stage) {
		case "localhost":
		case "local":
			return "localhost";
		case "production":
			return "production";
		case "demo":
		case "sandbox":
			return "sandbox";
		case "preview":
			return "preview";
		default:
			return "staging";
	}
};

/** The environment stage this page was served by, e.g. "production", "dev-luke". */
export const getStage = (): string => {
	if (isLocalHost()) {
		return "localhost";
	}
	return getPyEnvironment().stage || "production";
};

export const getEnvironment = (): string => getEnvironmentFromStage(getStage());

/**
 * How this environment composes hostnames. Falls back to production because the
 * bundle is shared across environments and a missing injection must not point a
 * deployed page at a dev host.
 */
export const getDomainConfig = (): DomainConfig => getPyEnvironment().domain ?? PRODUCTION_DOMAIN;

/** Hostname of a sibling service in this environment, e.g. "internal-api". */
export const getServiceDomain = (service?: string): string => buildDomain(getDomainConfig(), service);
